import React, { useEffect } from 'react';
import { QueryClient, QueryClientProvider, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import {
  i18nChangeLanguage,
  isI18nLanguageReady,
  normalizeLanguageCode,
} from './i18n';
import {
  IDENTITY_QUERY_KEY,
  isIdentityFetchAllowed,
  isOneIdHostname,
  useIdentity,
} from './hooks/useIdentity';
import { useRevenueIdentity } from './hooks/useRevenueIdentity';
import { LanguagePreferenceProvider } from './contexts/LanguagePreferenceContext';
import { resolvePreferredLanguage } from './utils/resolvePreferredLanguage';

const client = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: Infinity,
    },
  },
});

const InnerQCP = ({ children }) => {
  /* There are times where the language cookie is stale,
   * so it cannot be depended on. The value returned from Billing
   * does not have this issue.
   */
  const queryClient = useQueryClient();
  const { data: identityData } = useIdentity();

  const { revenueUserLanguage } = useRevenueIdentity();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (isOneIdHostname() && isIdentityFetchAllowed()) {
      void queryClient.refetchQueries(IDENTITY_QUERY_KEY);
    }
  }, [queryClient]);

  useEffect(() => {
    const targetLanguage = resolvePreferredLanguage({
      billingLanguage: identityData?.language,
      revenueUserLanguage,
    });
    void i18nChangeLanguage(targetLanguage);
  }, [identityData?.language, revenueUserLanguage]);

  const targetLanguage = normalizeLanguageCode(
    resolvePreferredLanguage({
      billingLanguage: identityData?.language,
      revenueUserLanguage,
    }),
  );
  const isLanguageReady = isI18nLanguageReady({
    i18n,
    targetLanguage,
  });

  return (
    <LanguagePreferenceProvider
      preferredLanguage={targetLanguage}
      isLanguageReady={isLanguageReady}
    >
      {children}
    </LanguagePreferenceProvider>
  );
};

export default ({ children }) => {
  return (
    <QueryClientProvider client={client}>
      <InnerQCP>{children}</InnerQCP>
    </QueryClientProvider>
  );
};
